import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Bolt, ChartNetwork, FolderGit2, LibraryBig } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "../ui/sidebar";
import VizSidebarFooter from "./viz-sidebar-footer";

const menu = [
  {
    title: "Overview",
    url: "/overview",
    icon: ChartNetwork,
  },
  {
    title: "Projects",
    url: "/projects",
    icon: FolderGit2,
  },
  {
    title: "Docs",
    url: "https://docs.vizops.io",
    icon: LibraryBig,
  },
  {
    title: "Settings",
    url: "/settings",
    icon: Bolt,
    subMenu: [
      {
        title: "Account",
        url: "/settings/account",
      },
    ],
  },
];

const SidebarLink = ({ item }) => {
  const { pathname } = useLocation();

  const [isHovered, setIsHovered] = useState(false); // Add state for individual hover

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const isActive = pathname === item.url;
  return (
    <SidebarMenuItem
      key={item.title}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarMenuButton
        asChild
        className={`
          m-auto
          ${
            pathname === item.url
              ? "text-primary hover:bg-sidebar-primary hover:text-primary"
              : ""
          }
        `}
      >
        <Link
          to={item.url}
          target={item.url.startsWith("http") ? "_blank" : ""}
        >
          <item.icon
            size="24"
            color={isActive ? "#ED5F00" : isHovered ? "white" : "gray"}
          />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const SidebarLinkWithSubMenu = ({ item }) => {
  const { pathname } = useLocation();

  const [isHovered, setIsHovered] = useState(false); // Add state for individual hover

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(pathname.includes(item.url));
  }, [pathname, item.url]);

  const { toggleSidebar, state } = useSidebar();

  const isActive = pathname.includes(item.url);
  return (
    <Collapsible
      key={item.title}
      asChild
      defaultOpen={isActive}
      open={isOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem
        key={item.title}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <CollapsibleTrigger
          asChild
          onClick={() => {
            setIsOpen(!isOpen);
            if (state === "collapsed") toggleSidebar();
          }}
        >
          <SidebarMenuButton
            className={`
            m-auto
            bg-muted
            data-[state=open]:hover:bg-muted
            ${
              isActive
                ? "text-primary hover:bg-sidebar-primary hover:text-primary data-[state=open]:hover:bg-muted data-[state=open]:hover:text-primary"
                : ""
            }
          `}
          >
            <item.icon
              size="24"
              color={isActive ? "#ED5F00" : isHovered ? "white" : "gray"}
            />
            <span>{item.title}</span>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.subMenu.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuButton
                  asChild
                  className={`
                          m-auto
                          data-[state=open]:hover:bg-muted
                          hover:bg-muyted
                ${
                  pathname === subItem.url
                    ? "text-primary hover:bg-sidebar-primary hover:text-primary "
                    : ""
                }
              `}
                >
                  <Link to={subItem.url}>
                    <span className="pl-6">{subItem.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

export function VizSidebar() {
  const { isMobile, state } = useSidebar();
  const isCollapsed = useMemo(() => state === "collapsed", [state]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b h-[3.6rem]">
        <div
          className={`flex items-center ${
            isCollapsed ? "justify-center" : ""
          } h-full`}
        >
          <a
            href="https://vizops.io"
            className={`flex items-center ${
              isCollapsed ? "justify-center" : ""
            } h-full relative`}
            aria-label="vizOps"
            rel="noopener noreferrer"
            target="_blank"
          >
            <img src="/favicon.svg" alt="Logo" className="h-8" />
          </a>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menu.map((item) =>
                item.subMenu ? (
                  <SidebarLinkWithSubMenu key={item.title} item={item} />
                ) : (
                  <SidebarLink key={item.title} item={item} />
                )
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <VizSidebarFooter isMobile={isMobile} />
    </Sidebar>
  );
}
